import React from "react";
import { IFileUploadResponseData } from "../../../../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../../../../api/interceptors";
import { IDocumentSettings } from "../../../../../components/course-author/types";
import { isInvalidImg, mkImageUrl } from "../image/ImagePart";
import { allowedTypesFormats } from "../../../../../App/Common/Styles/DNDImage";
import CustomModal, { ModalType } from "../../../../../components/utils/CustomModal";
import { DocumentAddButton, ImportURLContainer } from "./DocumentPart.styles";
import { FaPlus } from "react-icons/fa";
import { SeparatorErrorMessage } from "../separator/SpacerPart.styles";
import { PostUploadComponent } from "./PostUploadComponent";
import { DropZoneComponent, renderDropZone, renderLinkInput } from "./Dropzone";
import { MAX_FILE_SIZE } from "../../../../../utils/index";

interface BackgroundImageModalProps {
  showModal: boolean;
  closeModal: () => void;
  settings: IDocumentSettings["settings"];
  uploadBgImgProgress: number;
  onCancelBgImageUpload: () => void;
  bgImageErrorMessage: string;
  modalTitle: string;
  children: React.ReactNode;
}

export const BackgroundImageModal: React.FC<BackgroundImageModalProps> = props => {
  const {
    showModal,
    closeModal,
    settings,
    uploadBgImgProgress,
    onCancelBgImageUpload,
    bgImageErrorMessage,
    children,
    modalTitle,
  } = props;
  return (
    <CustomModal
      showModal={showModal}
      closeModal={closeModal}
      type={ModalType.Info}
      title={modalTitle}
      customCardProps={{ minWidth: "50%" }}
      customBodyProps={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <SeparatorErrorMessage>{bgImageErrorMessage}</SeparatorErrorMessage>
      {!settings.backgroundImageId ? (
        children
      ) : (
        <PostUploadComponent
          fileName={settings.backgroundImageName}
          fileSize={settings.backgroundImageSize}
          uploadProgress={uploadBgImgProgress}
          url={mkImageUrl(settings.backgroundImageId)}
          onCancelUpload={onCancelBgImageUpload}
        />
      )}
    </CustomModal>
  );
};

interface UploadBackgroundImageContentProps {
  settings: IDocumentSettings["settings"];
  updateSettings: (settings: IDocumentSettings["settings"]) => void;
  uploadFile: (
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  onUploadBgImgProgress: (progressEvent: ProgressEvent) => void;
  setBgImageErrorMessage: (message: string) => void;
  closeModal: () => void;
}

export const UploadBackgroundImageContent: React.FC<UploadBackgroundImageContentProps> = props => {
  const { uploadFile, settings, updateSettings, onUploadBgImgProgress, setBgImageErrorMessage, closeModal } = props;

  const handleBackgroundImageUpload = (files: File[] | undefined) => {
    setBgImageErrorMessage("");

    if (isInvalidImg(files)) {
      return alert(`Make sure to upload only one image\nFile type must be one of these: ${allowedTypesFormats}`);
    }
    if (files![0].size > MAX_FILE_SIZE) {
      return setBgImageErrorMessage("File Exceeds Maximum Size Limit");
    }
    uploadFile(files![0], onUploadBgImgProgress).then(([error, response]) => {
      if (error) {
        return setBgImageErrorMessage("No response");
      }
      updateSettings({
        ...settings,
        backgroundImageId: response!.data[0].mediaId.slice(4),
        backgroundImageName: files![0].name,
        backgroundImageSize: files![0].size,
      });
      closeModal();
    });
  };

  return renderDropZone(handleBackgroundImageUpload, <BackgroundImageDropZone />);
};

const BackgroundImageDropZone = () => (
  <DropZoneComponent supportedFilesTypes={["PNG", "JPG", "JPEG", "GIF"]} supportedFilesMbSize={10} />
);

interface LinkToImageContentProps {
  settings: IDocumentSettings["settings"];
  updateSettings: (settings: IDocumentSettings["settings"]) => void;
  uploadFileViaUrl: (
    link: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
  onUploadBgImgProgress: (progressEvent: ProgressEvent) => void;
  setBgImageErrorMessage: (message: string) => void;
  closeModal: () => void;
}

export const LinkToImageContent: React.FC<LinkToImageContentProps> = props => {
  const {
    settings,
    updateSettings,
    uploadFileViaUrl,
    onUploadBgImgProgress,
    setBgImageErrorMessage,
    closeModal,
  } = props;

  const [bgImageFromURL, setBgImageFromURL] = React.useState<string>("");

  const onChangeImgFromURL = (e: React.ChangeEvent<HTMLInputElement>) => setBgImageFromURL(e.target.value);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleImageUploadViaURL = () => {
    uploadFileViaUrl(bgImageFromURL, onUploadBgImgProgress).then(([error, response]) => {
      if (error) {
        return setBgImageErrorMessage("No response");
      }
      closeModal();
      updateSettings({
        ...settings,
        backgroundImageId: response!.data.mediaId.slice(4),
        backgroundImageName: response!.data.name,
      });
    });
  };

  return (
    <ImportURLContainer>
      {renderLinkInput(inputRef, bgImageFromURL, onChangeImgFromURL)}
      <DocumentAddButton onClick={handleImageUploadViaURL} data-testid="upload-url-button">
        <span>
          <FaPlus /> Add
        </span>
      </DocumentAddButton>
    </ImportURLContainer>
  );
};
